import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SectionCard, Money } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Lightbulb, AlertTriangle, TrendingUp, Info, Plus, Trash2, Save, Target, ClipboardList,
} from "lucide-react";

const SEV = {
  danger: { icon: AlertTriangle, cls: "border-[hsl(var(--danger)/0.35)] bg-[hsl(var(--danger)/0.07)] text-[hsl(var(--danger))]" },
  warning: { icon: AlertTriangle, cls: "border-[hsl(var(--warning)/0.35)] bg-[hsl(var(--warning)/0.08)] text-[hsl(var(--warning))]" },
  success: { icon: TrendingUp, cls: "border-[hsl(var(--success)/0.35)] bg-[hsl(var(--success)/0.08)] text-[hsl(var(--success))]" },
  info: { icon: Info, cls: "border-[hsl(var(--info)/0.35)] bg-[hsl(var(--info)/0.07)] text-[hsl(var(--info))]" },
};
const TAGS = ["priority", "campaign", "budget", "risk", "idea"];
const uid = () => Math.random().toString(36).slice(2, 9);

export function StrategySection({ strategy, groups, money, canEdit }) {
  const [notes, setNotes] = useState([]);
  const [meta, setMeta] = useState({ updated_at: "", updated_by: "" });
  const [dirty, setDirty] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.strategyNotes().then((d) => { setNotes(d.items || []); setMeta({ updated_at: d.updated_at, updated_by: d.updated_by }); }).catch(() => {});
  }, []);

  const rows = (groups || []).flatMap((g) => g.rows.map((r) => ({ ...r, portfolio: g.portfolio })));
  const s = strategy || {};
  const variancePct = Math.round((s.plan_variance_pct || 0) * 100);
  const burnPct = Math.round((s.burn_pct || 0) * 100);

  const update = (id, patch) => { setNotes((n) => n.map((x) => x.id === id ? { ...x, ...patch } : x)); setDirty(true); };
  const addNote = () => { setNotes((n) => [...n, { id: uid(), title: "", body: "", tag: "priority", done: false }]); setDirty(true); };
  const removeNote = (id) => { setNotes((n) => n.filter((x) => x.id !== id)); setDirty(true); };

  const doSave = async () => {
    setSaving(true);
    try {
      await api.saveStrategyNotes({ items: notes.filter((n) => n.title.trim() || n.body.trim()), reason: reason || "strategy update" });
      toast.success("Strategy notes saved");
      setDirty(false); setConfirmOpen(false); setReason("");
      const d = await api.strategyNotes(); setMeta({ updated_at: d.updated_at, updated_by: d.updated_by });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save notes"); }
    finally { setSaving(false); }
  };

  return (
    <SectionCard
      title="Strategizing for Marketing Leaders"
      description="Plan the full-year campaign spend, see where the plan runs above allocation, and capture the game-plan."
      testId="card-mh-strategy"
    >
      <div className="space-y-5 p-4">
        {/* budget-health strip */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="strategy-health-strip">
          <div className="rounded-xl border bg-card p-3.5">
            <p className="text-[11px] font-medium text-muted-foreground">Allocated vs Planned</p>
            <p className="mt-1 font-heading text-lg font-semibold tabular-nums">{money(s.total_allocated || 0)} <span className="text-sm font-normal text-muted-foreground">vs</span> {money(s.total_planned || 0)}</p>
            <Badge variant="outline" className={"mt-1.5 " + (variancePct > 0 ? "border-[hsl(var(--warning)/0.4)] text-[hsl(var(--warning))]" : "border-[hsl(var(--success)/0.4)] text-[hsl(var(--success))]")} data-testid="strategy-plan-variance">
              {variancePct > 0 ? `Plan ${variancePct}% above allocation` : `Plan uses ${Math.round((s.total_allocated ? (s.total_planned || 0) / s.total_allocated : 0) * 100)}% of allocation`}
            </Badge>
          </div>
          <div className="rounded-xl border bg-card p-3.5">
            <p className="text-[11px] font-medium text-muted-foreground">Budget Burn</p>
            <p className="mt-1 font-heading text-lg font-semibold tabular-nums">{burnPct}%</p>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--surface-3))]">
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, burnPct)}%`, background: burnPct >= 90 ? "hsl(var(--danger))" : burnPct >= 70 ? "hsl(var(--warning))" : "hsl(var(--success))" }} />
            </div>
          </div>
          <div className="rounded-xl border bg-card p-3.5">
            <p className="text-[11px] font-medium text-muted-foreground">Projected Full-Period Spend</p>
            <p className="mt-1 font-heading text-lg font-semibold tabular-nums">{money(s.projected_spend || 0)}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">Spare now: <span className="tabular-nums">{money(s.spare || 0)}</span></p>
          </div>
        </div>

        {/* auto-insights */}
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2" data-testid="strategy-insights">
          {(s.insights || []).map((ins, i) => {
            const sv = SEV[ins.severity] || SEV.info; const Icon = sv.icon;
            return (
              <div key={i} className={"flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-xs " + sv.cls} data-testid={`strategy-insight-${i}`}>
                <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                <div><p className="font-semibold">{ins.title}</p><p className="opacity-90">{ins.detail}</p></div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {/* FY planning table */}
          <div className="rounded-xl border">
            <div className="flex items-center gap-2 border-b bg-[hsl(var(--surface-2))] px-3 py-2">
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-semibold">Annual Campaign Planning</p>
            </div>
            <div className="table-scroll" style={{ maxHeight: 320 }}>
              <table className="w-full border-collapse" data-testid="strategy-planning-table">
                <thead><tr>
                  {["Project", "Allocated", "Planned", "Spent", "Spare", "Status"].map((h, i) => (
                    <th key={h} className={`th-sticky px-2.5 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ${i === 0 ? "th-sticky-left text-left" : "text-right"}`}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.project} data-testid={`strategy-plan-row-${r.project}`} className="border-b last:border-0">
                      <td className="td-sticky-left px-2.5 py-2 text-xs font-medium">{r.project}</td>
                      <td className="px-2.5 py-2 num text-xs text-muted-foreground"><Money value={r.allocated} /></td>
                      <td className="px-2.5 py-2 num text-xs"><Money value={r.planned} /></td>
                      <td className="px-2.5 py-2 num text-xs font-medium"><Money value={r.spent} /></td>
                      <td className="px-2.5 py-2 num text-xs"><Money value={r.spare} colorNegative /></td>
                      <td className="px-2.5 py-2 text-right">
                        {r.status === "over_plan" ? <Badge variant="outline" className="border-[hsl(var(--warning)/0.4)] text-[9px] text-[hsl(var(--warning))]">Above plan</Badge>
                          : r.status === "over_spent" ? <Badge variant="outline" className="border-[hsl(var(--danger)/0.4)] text-[9px] text-[hsl(var(--danger))]">Overspent</Badge>
                            : <Badge variant="outline" className="border-[hsl(var(--success)/0.4)] text-[9px] text-[hsl(var(--success))]">On track</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes board */}
          <div className="rounded-xl border">
            <div className="flex items-center justify-between border-b bg-[hsl(var(--surface-2))] px-3 py-2">
              <div className="flex items-center gap-2"><Target className="h-4 w-4 text-muted-foreground" /><p className="text-sm font-semibold">Game Plan &amp; Priorities</p></div>
              {canEdit && (
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={addNote} data-testid="strategy-add-note"><Plus className="mr-1 h-3.5 w-3.5" />Note</Button>
                  <Button size="sm" className="h-7 px-2" disabled={!dirty} onClick={() => setConfirmOpen(true)} data-testid="strategy-save-notes"><Save className="mr-1 h-3.5 w-3.5" />Save</Button>
                </div>
              )}
            </div>
            <div className="max-h-[320px] space-y-2.5 overflow-y-auto p-3" data-testid="strategy-notes-list">
              {notes.length === 0 && <div className="flex flex-col items-center gap-1 py-8 text-center text-xs text-muted-foreground"><Lightbulb className="h-5 w-5" />{canEdit ? "Add your first campaign priority or budget note." : "No strategy notes yet."}</div>}
              {notes.map((n) => (
                <div key={n.id} className={"rounded-lg border p-2.5 " + (n.done ? "bg-[hsl(var(--surface-2))] opacity-70" : "bg-card")} data-testid={`strategy-note-${n.id}`}>
                  {canEdit ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={n.done} onCheckedChange={(v) => update(n.id, { done: !!v })} data-testid={`strategy-note-done-${n.id}`} />
                        <Input value={n.title} placeholder="Title (e.g. Q2 Digital push for Edition)" onChange={(e) => update(n.id, { title: e.target.value })} className="h-7 flex-1 text-sm font-medium" data-testid={`strategy-note-title-${n.id}`} />
                        <select value={n.tag} onChange={(e) => update(n.id, { tag: e.target.value })} className="h-7 rounded-md border bg-background px-1.5 text-xs" data-testid={`strategy-note-tag-${n.id}`}>
                          {TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-[hsl(var(--danger))]" onClick={() => removeNote(n.id)} data-testid={`strategy-note-remove-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                      <Textarea value={n.body} placeholder="Details / next actions…" onChange={(e) => update(n.id, { body: e.target.value })} className="min-h-[52px] text-xs" data-testid={`strategy-note-body-${n.id}`} />
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[9px] uppercase">{n.tag}</Badge>
                        <p className={"text-sm font-medium " + (n.done ? "line-through" : "")}>{n.title}</p>
                      </div>
                      {n.body && <p className="mt-1 text-xs text-muted-foreground">{n.body}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {meta.updated_by && <p className="border-t px-3 py-1.5 text-[10px] text-muted-foreground">Last updated by {meta.updated_by}</p>}
          </div>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="strategy-save-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Save strategy notes</AlertDialogTitle>
            <AlertDialogDescription>This change is recorded in the audit log. Add a short reason.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. Q2 planning review)" data-testid="strategy-save-reason" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="strategy-save-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doSave(); }} disabled={saving} data-testid="strategy-save-confirm">{saving ? "Saving…" : "Save"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}
